<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: [String, Array],
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const conversationAttributes = useMapGetter(
  'attributes/getConversationAttributes'
);
const agents = useMapGetter('agents/getAgents');

const variableOptions = computed(() => {
  const options = [
    {
      id: '{{contact.email}}',
      name: t('AUTOMATION.ACTION.EMAIL_VARIABLES.CONTACT_EMAIL'),
    },
  ];

  (agents.value || [])
    .filter(agent => agent.email)
    .forEach(agent => {
      options.push({
        id: agent.email,
        name: `${t('AUTOMATION.ACTION.EMAIL_VARIABLES.AGENT_EMAIL')} › ${
          agent.name || agent.email
        }`,
      });
    });

  (contactAttributes.value || [])
    .filter(attr => attr.attributeDisplayType === 'text')
    .forEach(attr => {
      options.push({
        id: `{{contact.custom_attribute.${attr.attributeKey}}}`,
        name: `${t('AUTOMATION.ACTION.EMAIL_VARIABLES.CONTACT_ATTR')} › ${attr.attributeDisplayName}`,
      });
    });

  (conversationAttributes.value || [])
    .filter(attr => attr.attributeDisplayType === 'text')
    .forEach(attr => {
      options.push({
        id: `{{conversation.custom_attribute.${attr.attributeKey}}}`,
        name: `${t('AUTOMATION.ACTION.EMAIL_VARIABLES.CONVERSATION_ATTR')} › ${attr.attributeDisplayName}`,
      });
    });

  return options;
});

const isLiquidVariable = value => /^\{\{.+\}\}$/.test(value);
const isQuickPickValue = value =>
  !!value && variableOptions.value.some(option => option.id === value);

const currentValue = computed(() => {
  if (Array.isArray(props.modelValue)) return props.modelValue[0] || '';
  return props.modelValue || '';
});

const mode = ref(
  isLiquidVariable(currentValue.value) || isQuickPickValue(currentValue.value)
    ? 'variable'
    : 'fixed'
);

const selectedVariable = computed({
  get() {
    const val = currentValue.value;
    const found = variableOptions.value.find(v => v.id === val);
    if (found) return found;
    if (isLiquidVariable(val)) return { id: val, name: val };
    return null;
  },
  set(value) {
    emit('update:modelValue', value?.id || '');
  },
});

const fixedEmail = computed({
  get() {
    return isLiquidVariable(currentValue.value) ||
      isQuickPickValue(currentValue.value)
      ? ''
      : currentValue.value;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const setMode = newMode => {
  if (newMode === mode.value) return;
  mode.value = newMode;
  emit('update:modelValue', '');
};

watch(
  () => props.modelValue,
  val => {
    const v = Array.isArray(val) ? val[0] || '' : val || '';
    if (
      (isLiquidVariable(v) || isQuickPickValue(v)) &&
      mode.value !== 'variable'
    ) {
      mode.value = 'variable';
    }
  }
);
</script>

<template>
  <div class="flex flex-col gap-1 flex-1 min-w-0">
    <div class="flex justify-end">
      <div class="flex rounded bg-n-alpha-1 p-0.5">
        <button
          class="px-2 py-0.5 text-xs font-semibold rounded transition-all"
          :class="[
            mode === 'fixed'
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="setMode('fixed')"
        >
          {{ t('AUTOMATION.ACTION.EMAIL_MODE_FIXED') }}
        </button>
        <button
          class="px-2 py-0.5 text-xs font-semibold rounded transition-all"
          :class="[
            mode === 'variable'
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="setMode('variable')"
        >
          {{ t('AUTOMATION.ACTION.EMAIL_MODE_VARIABLE') }}
        </button>
      </div>
    </div>
    <SingleSelect
      v-if="mode === 'variable'"
      v-model="selectedVariable"
      :options="variableOptions"
      :placeholder="t('AUTOMATION.ACTION.EMAIL_VARIABLE_PLACEHOLDER')"
    />
    <NextInput
      v-else
      v-model="fixedEmail"
      type="email"
      size="sm"
      :placeholder="t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
    />
  </div>
</template>
